import { useEffect, useRef, useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { IT_EMAIL, ITSM_URL } from "../lib/contacts";

const EXT_LABEL = "Ext. 310";

/** Floating IT-support widget (dD) — kd-support-widget with a "?" toggle button. */
export function HelpWidget({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) setOpen(false);
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (document.activeElement?.tagName || "").toUpperCase();
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
          e.preventDefault();
          setOpen((o) => !o);
        }
      }
    }
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="kd-support-widget" ref={ref} dir={lang === "ar" ? "rtl" : "ltr"}>
      {open && (
        <div className="kd-support-panel" role="dialog" aria-label={t("قسم تقنية المعلومات", "IT Support")}>
          <p className="kd-support-title">{t("قسم تقنية المعلومات", "IT Support")}</p>
          <p className="kd-support-copy">
            {t(
              "في حال واجهتك مشكلة في تسجيل الدخول أو لم تتضح لك إحدى الخطوات، يُرجى استخدام بيانات التواصل التالية للوصول إلى الفريق المختص.",
              "If you encounter a sign-in issue or are unsure about any step, please use the following contact details to reach the appropriate support team.",
            )}
          </p>
          <a href={ITSM_URL} target="_blank" rel="noopener noreferrer" className="kd-support-row kd-ticket-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
              <path d="M13 5v2" />
              <path d="M13 17v2" />
              <path d="M13 11v2" />
            </svg>
            <span>{t("فتح تذكرة دعم فني", "Open a support ticket")}</span>
          </a>
          <div className="kd-divider" />
          <a href={`mailto:${IT_EMAIL}`} className="kd-support-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            <span>{IT_EMAIL}</span>
          </a>
          <span className="kd-support-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.7 19.79 19.79 0 0 1 1.61 5.1 2 2 0 0 1 3.58 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.91a16 16 0 0 0 6.06 6.06l1.97-1.97a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <span>{EXT_LABEL}</span>
          </span>
        </div>
      )}
      <button
        className="kd-support-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        aria-label={t("قسم تقنية المعلومات", "IT Support")}
        title={t("هل تحتاج إلى مساعدة؟", "Need help?")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>
    </div>
  );
}
